import "./AgentScoringRulePage.less";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import round from "lodash/round";
import isEqual from "lodash/isEqual";
import debounce from "lodash/debounce";
import {
    GuxButtonSlot,
    GuxDropdown,
    GuxFormFieldDropdown,
    GuxFormFieldNumber,
    GuxListbox,
    GuxOption,
    GuxRadialLoading,
} from "genesys-spark-components-react";
import {
    AgentScoringRuleAgentToScoreEnum,
    AgentScoringRuleSamplingTypeEnum,
    AgentScoringRuleSubmissionTypeEnum,
} from "@genesys/cloud-frontend/models";
import type { AgentScoringRule } from "@genesys/cloud-frontend/models";
import { useStrings } from "features/i18n/useStrings";
import strings from "./AgentScoringRulePage.strings";
import commonStrings from "./agentScoringRulesCommon.strings";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import {
    createAgentScoringRule,
    getAgentScoringRuleById,
    updateAgentScoringRule,
} from "./agentScoringRulesSlice";
import type { SaveRejectValue } from "./agentScoringRulesSlice";
import {
    getEvaluationFormsByContextIds,
    searchEvaluationForms,
} from "./evaluationFormLookup";
import type { EvaluationFormOption } from "./evaluationFormLookup";
import agentScoringRulesToasts from "./agentScoringRulesToasts";
import {
    DEFAULT_AGENT_TO_SCORE_FOR_NEW_RULE,
    DEFAULT_AGENT_TO_SCORE_WHEN_MISSING,
    isSamplingPercentageValid,
} from "./agentScoringRuleUtils";
import { AgentScoringRuleRadioGroup } from "./AgentScoringRuleRadioGroup";
import { toGuxDisabled } from "util/componentUtil";

const DEBOUNCE_TIMEOUT = 200;

/**
 * Draft used by the editor form. Mirrors the writable subset of
 * {@link AgentScoringRule}; server-managed fields (id, programId, dateCreated,
 * etc.) are omitted because they are supplied via path params or ignored by the
 * backend on write.
 */
interface AgentScoringRuleDraft {
    samplingType?: AgentScoringRuleSamplingTypeEnum;
    samplingPercentage?: number | null;
    submissionType: AgentScoringRuleSubmissionTypeEnum;
    evaluationFormContextId: string;
    enabled: boolean;
    agentToScore?: AgentScoringRuleAgentToScoreEnum;
}

interface FormDropdownItem {
    text: string;
    value: string;
    disabled: boolean;
}

function emptyDraft(): AgentScoringRuleDraft {
    return {
        samplingType: undefined,
        samplingPercentage: null,
        submissionType: AgentScoringRuleSubmissionTypeEnum.Automated,
        evaluationFormContextId: "",
        enabled: true,
    };
}

function draftFromRule(rule: AgentScoringRule): AgentScoringRuleDraft {
    return {
        samplingType: rule.samplingType,
        samplingPercentage: rule.samplingPercentage ?? null,
        submissionType:
            rule.submissionType ?? AgentScoringRuleSubmissionTypeEnum.Automated,
        evaluationFormContextId: rule.evaluationFormContextId ?? "",
        enabled: rule.enabled ?? true,
        agentToScore: rule.agentToScore,
    };
}

/** Normalizes a draft for dirty comparison (ignore percentage unless sampling is Percentage). */
function normalizeForCompare(
    draft: AgentScoringRuleDraft
): AgentScoringRuleDraft {
    const isPercentage =
        draft.samplingType === AgentScoringRuleSamplingTypeEnum.Percentage;
    return {
        ...draft,
        samplingPercentage: isPercentage ? draft.samplingPercentage : undefined,
    };
}

function buildEvaluationFormItems(
    forms: EvaluationFormOption[],
    opts: {
        selectedContextId: string;
        searchText: string;
        restricted: boolean;
        selectedName: string;
        restrictedLabel: string;
    }
): FormDropdownItem[] {
    const items: FormDropdownItem[] = forms.map((form) => ({
        text: form.name || form.contextId,
        value: form.contextId,
        disabled: false,
    }));
    items.sort((a, b) =>
        a.text.localeCompare(b.text, undefined, { sensitivity: "base" })
    );

    const { selectedContextId, searchText, restricted, selectedName } = opts;
    if (selectedContextId && !searchText) {
        if (restricted) {
            items.unshift({
                text: opts.restrictedLabel,
                value: selectedContextId,
                disabled: true,
            });
        } else {
            const selectedForm = forms.find(
                (form) => form.contextId === selectedContextId
            );
            const selectedIndex = items.findIndex(
                (item) => item.value === selectedContextId
            );
            const pinnedItem: FormDropdownItem = {
                text: selectedForm?.name || selectedName || selectedContextId,
                value: selectedContextId,
                disabled: true,
            };
            if (selectedIndex > 0) {
                items.splice(selectedIndex, 1);
                items.unshift(pinnedItem);
            } else if (selectedIndex === 0) {
                items[0] = pinnedItem;
            } else {
                items.unshift(pinnedItem);
            }
        }
    }

    return items;
}

interface AgentScoringRulePageProps {
    programId: string;
    /** Rule id when editing; undefined when creating a new rule. */
    ruleId?: string;
    /** Invoked on cancel and after a successful save (navigate back to table). */
    onClose: () => void;
}

export function AgentScoringRulePage({
    programId,
    ruleId,
    onClose,
}: AgentScoringRulePageProps) {
    const t = useStrings(strings);
    const common = useStrings(commonStrings);
    const dispatch = useAppDispatch();
    const isEditMode = !!ruleId;

    const isEvaluationFocusEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.WEM_1511_EVALUATION_FOCUS)
    );
    const isDivisionAwareEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.DIVISION_AWARE_EVALS_SURVEYS)
    );

    // `rule` is mirrored to a ref so async flows read the latest value
    // synchronously (React state updates are async).
    const [rule, setRuleState] = useState<AgentScoringRuleDraft>(emptyDraft);
    const ruleRef = useRef(rule);
    const setRule = useCallback(
        (
            next:
                | AgentScoringRuleDraft
                | ((prev: AgentScoringRuleDraft) => AgentScoringRuleDraft)
        ) => {
            const resolved =
                typeof next === "function" ? next(ruleRef.current) : next;
            ruleRef.current = resolved;
            setRuleState(resolved);
        },
        []
    );

    const [originalDraft, setOriginalDraft] =
        useState<AgentScoringRuleDraft>(emptyDraft);
    // Edit mode starts loading so the empty form is not interactive before data arrives.
    const [isPageLoading, setIsPageLoading] = useState(isEditMode);
    const [isSaving, setIsSaving] = useState(false);
    const [duplicateRuleError, setDuplicateRuleError] = useState("");

    const [formItems, setFormItems] = useState<FormDropdownItem[]>([]);
    const [isFormLoading, setIsFormLoading] = useState(false);

    // Refs mirroring state read inside async/debounced flows.
    const formsRef = useRef<EvaluationFormOption[]>([]);
    const searchTextRef = useRef("");
    const hasAllFormsLoadedRef = useRef(false);
    const restrictedRef = useRef(false);
    const selectedNameRef = useRef("");

    const restrictedLabel = common.labels.restrictedByPermissions;

    const rebuildItems = useCallback(() => {
        setFormItems(
            buildEvaluationFormItems(formsRef.current, {
                selectedContextId: ruleRef.current.evaluationFormContextId,
                searchText: searchTextRef.current,
                restricted: restrictedRef.current,
                selectedName: selectedNameRef.current,
                restrictedLabel,
            })
        );
    }, [restrictedLabel]);

    const fetchForms = useCallback(
        async (name?: string): Promise<EvaluationFormOption[]> => {
            try {
                return await searchEvaluationForms(name);
            } catch (error) {
                console.error("Error fetching evaluation forms:", error);
                return [];
            }
        },
        []
    );

    const resolveInaccessibleFormName = useCallback(
        async (contextId: string): Promise<string> => {
            try {
                const forms = await getEvaluationFormsByContextIds([contextId]);
                return forms[0]?.name ?? "";
            } catch (error) {
                console.error("Error resolving deleted form name:", error);
                return "";
            }
        },
        []
    );

    const resolveMissingSelectedFormOnInit = useCallback(async () => {
        const contextId = ruleRef.current.evaluationFormContextId;
        if (!contextId) return;
        const selectedForm = formsRef.current.find(
            (form) => form.contextId === contextId
        );
        if (selectedForm) return;

        const inaccessibleFormName = await resolveInaccessibleFormName(
            contextId
        );
        if (!inaccessibleFormName && isDivisionAwareEnabled) {
            restrictedRef.current = true;
            selectedNameRef.current = restrictedLabel;
        } else {
            selectedNameRef.current = inaccessibleFormName;
        }
    }, [resolveInaccessibleFormName, isDivisionAwareEnabled, restrictedLabel]);

    const loadAllForms = useCallback(
        async (isFirstLoad = false) => {
            setIsFormLoading(true);
            try {
                const entities = await fetchForms();
                formsRef.current = entities;

                const contextId = ruleRef.current.evaluationFormContextId;
                if (contextId) {
                    const selectedForm = entities.find(
                        (form) => form.contextId === contextId
                    );
                    if (selectedForm) {
                        selectedNameRef.current = selectedForm.name;
                        restrictedRef.current = false;
                    }
                }

                if (isFirstLoad) {
                    await resolveMissingSelectedFormOnInit();
                }

                hasAllFormsLoadedRef.current = true;
                rebuildItems();
            } finally {
                setIsFormLoading(false);
            }
        },
        [fetchForms, rebuildItems, resolveMissingSelectedFormOnInit]
    );

    const runSearch = useCallback(
        async (searchText: string) => {
            if (!searchText.trim()) {
                if (!hasAllFormsLoadedRef.current) {
                    await loadAllForms();
                }
                return;
            }
            setIsFormLoading(true);
            try {
                const entities = await fetchForms(searchText.trim());
                formsRef.current = entities;
                hasAllFormsLoadedRef.current = false;
                rebuildItems();
            } finally {
                setIsFormLoading(false);
            }
        },
        [fetchForms, loadAllForms, rebuildItems]
    );

    // Stable debounced search that always calls the latest runSearch.
    const runSearchRef = useRef(runSearch);
    runSearchRef.current = runSearch;
    const debouncedSearch = useMemo(
        () =>
            debounce((text: string) => {
                void runSearchRef.current(text);
            }, DEBOUNCE_TIMEOUT),
        []
    );
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const loadRuleIfEditing =
        useCallback(async (): Promise<AgentScoringRuleDraft | null> => {
            if (!ruleId) return null;
            try {
                const fetched = await dispatch(
                    getAgentScoringRuleById({ programId, ruleId })
                ).unwrap();
                const draft = draftFromRule(fetched);
                if (isEvaluationFocusEnabled && !draft.agentToScore) {
                    draft.agentToScore = DEFAULT_AGENT_TO_SCORE_WHEN_MISSING;
                }
                setRule(draft);
                setOriginalDraft(draft);
                return draft;
            } catch (error) {
                console.error("Error loading agent scoring rule:", error);
                return null;
            }
        }, [dispatch, isEvaluationFocusEnabled, programId, ruleId, setRule]);

    const clearForm = useCallback(() => {
        const draft = emptyDraft();
        if (isEvaluationFocusEnabled) {
            draft.agentToScore = DEFAULT_AGENT_TO_SCORE_FOR_NEW_RULE;
        }
        setRule(draft);
        setOriginalDraft(draft);
        selectedNameRef.current = "";
        searchTextRef.current = "";
        restrictedRef.current = false;
        hasAllFormsLoadedRef.current = false;
        setDuplicateRuleError("");
    }, [isEvaluationFocusEnabled, setRule]);

    // Initial load: fetch rule (edit) or reset (create), then load forms.
    useEffect(() => {
        let active = true;
        async function init() {
            if (isEditMode) {
                setIsPageLoading(true);
                const draft = await loadRuleIfEditing();
                if (!active) return;
                // Match EvaluationForm / EvaluationRoute: surface a toast and
                // leave rather than rendering a blank editor after a failed load.
                if (!draft) {
                    agentScoringRulesToasts.loadFailure();
                    onClose();
                    return;
                }
                setIsPageLoading(false);
            } else {
                clearForm();
            }
            if (active) {
                await loadAllForms(true);
            }
        }
        void init();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programId, ruleId]);

    // ---- Derived ----
    const shouldShowSamplingDependentFields =
        rule.samplingType === AgentScoringRuleSamplingTypeEnum.All ||
        rule.samplingType === AgentScoringRuleSamplingTypeEnum.Percentage;

    const isSamplingPercentageInvalid =
        rule.samplingType === AgentScoringRuleSamplingTypeEnum.Percentage &&
        rule.samplingPercentage != null &&
        !Number.isNaN(rule.samplingPercentage) &&
        !isSamplingPercentageValid(rule.samplingPercentage);

    const isDirty = !isEqual(
        normalizeForCompare(rule),
        normalizeForCompare(originalDraft)
    );

    const isSaveDisabled = (() => {
        if (isSaving) return true;
        if (isEditMode && !isDirty) return true;
        if (
            !programId ||
            !rule.samplingType ||
            !rule.evaluationFormContextId ||
            duplicateRuleError
        ) {
            return true;
        }
        if (rule.samplingType === AgentScoringRuleSamplingTypeEnum.Percentage) {
            return !isSamplingPercentageValid(rule.samplingPercentage);
        }
        return false;
    })();
    const saveButtonDisabled = toGuxDisabled(isSaveDisabled);

    // ---- Handlers ----
    const handleSamplingTypeChange = (
        value: AgentScoringRuleSamplingTypeEnum
    ) => {
        setRule((prev) => ({ ...prev, samplingType: value }));
    };

    const handleAgentToScoreChange = (
        value: AgentScoringRuleAgentToScoreEnum
    ) => {
        setRule((prev) => ({ ...prev, agentToScore: value }));
    };

    const handlePercentageChange = (raw: string) => {
        setRule((prev) => ({
            ...prev,
            samplingPercentage: raw === "" ? null : Number(raw),
        }));
    };

    const handlePercentageBlur = () => {
        const value = ruleRef.current.samplingPercentage;
        if (value != null && !Number.isNaN(value)) {
            setRule((prev) => ({
                ...prev,
                samplingPercentage: round(value, 2),
            }));
        }
    };

    const handleFormSelectionChange = (value: string) => {
        if (!value) return;
        setDuplicateRuleError("");
        debouncedSearch.cancel();
        restrictedRef.current = false;
        searchTextRef.current = "";
        selectedNameRef.current =
            formItems.find((item) => item.value === value)?.text ?? "";
        setRule((prev) => ({ ...prev, evaluationFormContextId: value }));
        rebuildItems();
    };

    const handleFormFilter = (searchText: string) => {
        const text = (searchText || "").trim();
        searchTextRef.current = text;

        if (!text || text === selectedNameRef.current) {
            debouncedSearch.cancel();
            if (!hasAllFormsLoadedRef.current) {
                void loadAllForms();
            }
            return;
        }

        hasAllFormsLoadedRef.current = false;
        setIsFormLoading(true);
        debouncedSearch(text);
    };

    const saveRule = async () => {
        const current = ruleRef.current;
        const samplingType = current.samplingType;
        if (!samplingType) return;

        const isPercentage =
            samplingType === AgentScoringRuleSamplingTypeEnum.Percentage;
        const body: AgentScoringRule = {
            samplingType,
            submissionType: current.submissionType,
            evaluationFormContextId: current.evaluationFormContextId,
            enabled: current.enabled,
            samplingPercentage: isPercentage
                ? current.samplingPercentage ?? undefined
                : undefined,
            ...(isEvaluationFocusEnabled && current.agentToScore
                ? { agentToScore: current.agentToScore }
                : {}),
        };

        setIsSaving(true);
        setDuplicateRuleError("");
        try {
            const action =
                isEditMode && ruleId
                    ? await dispatch(
                          updateAgentScoringRule({
                              programId,
                              ruleId,
                              rule: body,
                          })
                      )
                    : await dispatch(
                          createAgentScoringRule({ programId, rule: body })
                      );

            if (
                createAgentScoringRule.fulfilled.match(action) ||
                updateAgentScoringRule.fulfilled.match(action)
            ) {
                if (isEditMode) {
                    agentScoringRulesToasts.updateSuccess();
                } else {
                    agentScoringRulesToasts.createSuccess();
                }
                onClose();
                return;
            }

            const status = (action.payload as SaveRejectValue | undefined)
                ?.status;
            if (status === 409) {
                // Duplicate-form conflict is shown inline on the dropdown.
                setDuplicateRuleError(t.errors.duplicateRule);
            } else if (isEditMode) {
                agentScoringRulesToasts.updateFailure();
            } else {
                agentScoringRulesToasts.createFailure();
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (rule.samplingType === AgentScoringRuleSamplingTypeEnum.All) {
            setRule((prev) => ({ ...prev, samplingPercentage: null }));
        }
        void saveRule();
    };

    if (isPageLoading) {
        return (
            <div className="AgentScoringRulePage">
                <div
                    className="AgentScoringRulePage-loading AgentScoringRulePage-loading--fullscreen"
                    role="status"
                >
                    <GuxRadialLoading screenreaderText={t.labels.loading} />
                </div>
            </div>
        );
    }

    return (
        <div className="AgentScoringRulePage">
            <form onSubmit={handleSubmit}>
                <div className="AgentScoringRulePage-content">
                    <h2 className="gux-heading-subheading">
                        {isEditMode
                            ? t.labels.editRuleTitle
                            : t.labels.newRuleTitle}
                    </h2>
                    <p className="AgentScoringRulePage-description">
                        {t.labels.createRuleDescription}
                    </p>

                    <div className="AgentScoringRulePage-fields">
                        <AgentScoringRuleRadioGroup
                            name="interactionVolume"
                            groupLabel={t.labels.interactionVolumeLabel}
                            value={rule.samplingType}
                            onChange={handleSamplingTypeChange}
                            options={[
                                {
                                    value: AgentScoringRuleSamplingTypeEnum.All,
                                    label: t.labels.allInteractionsOption,
                                    testId: "sampling-type-all",
                                },
                                {
                                    value: AgentScoringRuleSamplingTypeEnum.Percentage,
                                    label: t.labels
                                        .percentageInteractionsOption,
                                    testId: "sampling-type-percentage",
                                },
                            ]}
                        />

                        {shouldShowSamplingDependentFields &&
                            isEvaluationFocusEnabled && (
                                <AgentScoringRuleRadioGroup
                                    name="agentsToScore"
                                    groupLabel={
                                        common.labels.agentsToScore.label
                                    }
                                    value={rule.agentToScore}
                                    onChange={handleAgentToScoreChange}
                                    options={[
                                        {
                                            value: AgentScoringRuleAgentToScoreEnum.Each,
                                            label: common.labels.agentsToScore
                                                .each,
                                            testId: "agent-to-score-each",
                                        },
                                        {
                                            value: AgentScoringRuleAgentToScoreEnum.First,
                                            label: common.labels.agentsToScore
                                                .first,
                                            testId: "agent-to-score-first",
                                        },
                                        {
                                            value: AgentScoringRuleAgentToScoreEnum.Last,
                                            label: common.labels.agentsToScore
                                                .last,
                                            testId: "agent-to-score-last",
                                        },
                                    ]}
                                />
                            )}

                        {rule.samplingType ===
                            AgentScoringRuleSamplingTypeEnum.Percentage && (
                            <GuxFormFieldNumber labelPosition="above">
                                <label slot="label">
                                    {t.labels.setPercentageLabel}
                                </label>
                                <input
                                    slot="input"
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max="100"
                                    placeholder={
                                        t.labels.setPercentagePlaceholder
                                    }
                                    value={
                                        rule.samplingPercentage == null
                                            ? ""
                                            : rule.samplingPercentage
                                    }
                                    onChange={(event) =>
                                        handlePercentageChange(
                                            event.target.value
                                        )
                                    }
                                    onBlur={handlePercentageBlur}
                                    data-testid="sampling-percentage"
                                />
                                {isSamplingPercentageInvalid && (
                                    <span
                                        slot="error"
                                        role="alert"
                                        className="AgentScoringRulePage-error"
                                    >
                                        {t.errors.samplingPercentage}
                                    </span>
                                )}
                            </GuxFormFieldNumber>
                        )}

                        {shouldShowSamplingDependentFields && (
                            <GuxFormFieldDropdown>
                                <GuxDropdown
                                    filterType="custom"
                                    value={rule.evaluationFormContextId}
                                    loading={isFormLoading}
                                    hasError={!!duplicateRuleError}
                                    placeholder={
                                        t.labels.evaluationFormPlaceholder
                                    }
                                    onInput={(event) =>
                                        handleFormSelectionChange(
                                            (event.target as HTMLInputElement)
                                                .value
                                        )
                                    }
                                    onGuxfilter={(event) =>
                                        handleFormFilter(event.detail)
                                    }
                                    onGuxexpanded={() =>
                                        debouncedSearch.cancel()
                                    }
                                    onGuxcollapsed={() =>
                                        debouncedSearch.cancel()
                                    }
                                    data-testid="evaluation-form-dropdown"
                                >
                                    <GuxListbox
                                        aria-label={
                                            t.labels.evaluationFormLabel
                                        }
                                    >
                                        {formItems.map((item) => {
                                            const optionDisabled =
                                                toGuxDisabled(item.disabled);
                                            return (
                                                <GuxOption
                                                    key={item.value}
                                                    value={item.value}
                                                    disabled={optionDisabled}
                                                    aria-disabled={
                                                        optionDisabled
                                                    }
                                                >
                                                    {item.text}
                                                </GuxOption>
                                            );
                                        })}
                                    </GuxListbox>
                                </GuxDropdown>
                                <label slot="label">
                                    {t.labels.evaluationFormLabel}
                                </label>
                                {duplicateRuleError && (
                                    <span
                                        slot="error"
                                        role="alert"
                                        className="AgentScoringRulePage-error"
                                    >
                                        {duplicateRuleError}
                                    </span>
                                )}
                            </GuxFormFieldDropdown>
                        )}
                    </div>
                </div>

                <div className="AgentScoringRulePage-footer">
                    {/* Native submit button inside GuxButtonSlot is required for
                        HTML form submission (GuxButton does not submit forms). */}
                    <GuxButtonSlot accent="primary">
                        <button
                            type="submit"
                            disabled={saveButtonDisabled}
                            aria-disabled={saveButtonDisabled}
                            data-testid="save-rule-button"
                        >
                            {isEditMode
                                ? t.labels.saveRuleButton
                                : t.labels.addRuleButton}
                        </button>
                    </GuxButtonSlot>
                    <GuxButtonSlot accent="ghost">
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="cancel-rule-button"
                        >
                            {t.labels.cancelButton}
                        </button>
                    </GuxButtonSlot>
                </div>
            </form>
        </div>
    );
}

export default AgentScoringRulePage;
